import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Palmtree, Users, BedDouble, Maximize, ArrowUpRight } from "lucide-react";
import { api } from "../api";
import { useLang } from "../i18n";

export const HotelsSection = () => {
  const { lang, t } = useLang();
  const [hotels, setHotels] = useState([]);

  useEffect(() => {
    api.get("/apartments").then((r) => {
      setHotels(r.data.filter((a) => a.category_mk === "Хотели" || a.category_en === "Hotels"));
    }).catch(() => {});
  }, []);

  return (
    <section id="hoteli" data-testid="hotels-section" className="bg-sand text-ink py-24 md:py-36 border-t border-line/60">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className={hotels.length ? "mb-14 md:mb-20" : "text-center"}
        >
          <p className="text-terra text-xs font-bold tracking-[0.35em] uppercase mb-4">{hotels.length ? t("nav.hotels") : t("hotels.kicker")}</p>
          <h2 className="font-serif text-4xl md:text-6xl tracking-tight leading-none mb-6">{t("hotels.title")}</h2>
          {!hotels.length && (
            <p className="text-ink/60 text-sm md:text-base leading-relaxed max-w-md mx-auto mb-10">{t("hotels.sub")}</p>
          )}
        </motion.div>

        {hotels.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
            {hotels.map((hotel, i) => (
              <motion.div
                key={hotel.slug}
                initial={{ opacity: 0, y: 48 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: i * 0.12 }}
              >
                <Link
                  to={`/apartments/${hotel.slug}`}
                  data-testid={`hotel-card-${hotel.slug}`}
                  className="group block border border-line/70 bg-sand hover:shadow-[0_24px_60px_-24px_rgba(44,42,38,0.35)] transition-shadow duration-700"
                >
                  {hotel.images.length > 0 ? (
                    <div className="spotlight-frame aspect-[4/3]">
                      <img src={hotel.images[0]} alt={lang === "mk" ? hotel.name_mk : hotel.name_en} className="w-full h-full object-cover" loading="lazy" />
                      <div className="absolute top-4 left-4 bg-coal/80 backdrop-blur px-3 py-1.5 text-sand text-[11px] font-semibold tracking-widest uppercase">
                        {lang === "mk" ? hotel.location_mk : hotel.location_en}
                      </div>
                      {hotel.coming_soon && (
                        <div className="absolute top-4 right-4 bg-ember px-3 py-1.5 text-coal text-[11px] font-bold tracking-widest uppercase" data-testid={`hotel-coming-soon-${hotel.slug}`}>
                          {t("grid.comingSoon")}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="aspect-[4/3] bg-coal flex items-center justify-center">
                      <span className="font-serif italic text-3xl md:text-4xl text-ember" data-testid={`hotel-coming-soon-${hotel.slug}`}>{t("grid.comingSoon")}</span>
                    </div>
                  )}
                  <div className="p-6 md:p-8">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="font-serif text-2xl md:text-3xl tracking-tight group-hover:text-terra transition-colors duration-300">
                          {lang === "mk" ? hotel.name_mk : hotel.name_en}
                        </h3>
                        <p className="text-ink/55 text-sm mt-1">{lang === "mk" ? hotel.tagline_mk : hotel.tagline_en}</p>
                      </div>
                      <div className="w-10 h-10 shrink-0 border border-line flex items-center justify-center group-hover:bg-terra group-hover:border-terra group-hover:text-sand transition-colors duration-300">
                        <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
                      </div>
                    </div>
                    <div className="mt-6 pt-5 border-t border-line/70 flex items-center justify-between text-sm">
                      <div className="flex items-center gap-5 text-ink/60">
                        {hotel.max_guests > 0 && (
                          <span className="inline-flex items-center gap-1.5">
                            <Users className="w-4 h-4" strokeWidth={1.5} /> {hotel.max_guests} {t("grid.guests")}
                          </span>
                        )}
                        {hotel.bedrooms > 0 && (
                          <span className="inline-flex items-center gap-1.5">
                            <BedDouble className="w-4 h-4" strokeWidth={1.5} /> {hotel.bedrooms} {t("grid.bedrooms")}
                          </span>
                        )}
                        {hotel.size_m2 > 0 && (
                          <span className="hidden sm:inline-flex items-center gap-1.5">
                            <Maximize className="w-4 h-4" strokeWidth={1.5} /> {hotel.size_m2} m²
                          </span>
                        )}
                      </div>
                      {hotel.coming_soon ? (
                        <p className="font-serif text-xl italic text-terra whitespace-nowrap">{t("grid.comingSoon")}</p>
                      ) : (
                        <p className="font-serif text-xl whitespace-nowrap">
                          <span className="text-xs font-sans text-ink/50 mr-1">{t("grid.from")}</span>€{hotel.price_per_night}
                          <span className="text-xs font-sans text-ink/50">{t("grid.perNight")}</span>
                        </p>
                      )}
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center">
            <div className="flex flex-wrap justify-center gap-4">
              <div className="flex flex-col items-center gap-2">
                <a
                  href="https://wa.me/38978321872"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hotels-whatsapp-button"
                  className="inline-flex items-center gap-3 px-8 py-4 text-xs font-bold tracking-[0.2em] uppercase transition-transform duration-300 hover:scale-[1.03]"
                  style={{ backgroundColor: "#25D366", color: "#0b1f13" }}
                >
                  <img src="https://cdn.simpleicons.org/whatsapp/0b1f13" alt="WhatsApp" className="w-5 h-5" /> WhatsApp
                </a>
              </div>
              <div className="flex flex-col items-center gap-2">
                <a
                  href="viber://chat?number=%2B38978321872"
                  data-testid="hotels-viber-button"
                  className="inline-flex items-center gap-3 px-8 py-4 text-xs font-bold tracking-[0.2em] uppercase text-white transition-transform duration-300 hover:scale-[1.03]"
                  style={{ backgroundColor: "#7360F2" }}
                >
                  <img src="https://cdn.simpleicons.org/viber/ffffff" alt="Viber" className="w-5 h-5" /> Viber
                </a>
              </div>
            </div>
            <Palmtree className="w-6 h-6 text-terra/40 mx-auto mt-14" strokeWidth={1.5} />
          </div>
        )}
      </div>
    </section>
  );
};
